import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../../../lib/api';
import { usePermesso } from '../../../../context/PermessiContext';
import { CommessaDettaglio } from '../SchedaCommessa';
import { API_DOCS } from './TabDistinte.api';
import ApiInspector from '../../../../components/ApiInspector';

// ── Tipi ─────────────────────────────────────────────────────────────────────

type TipoDistinta     = 'MERCE' | 'LAVORAZIONI';
type AddebitoDistinta = 'NOSTRO' | 'CLIENTE';
type StatoDistinta    = 'BOZZA' | 'CONFERMATA';

interface DistintaListItem {
  id: number;
  commessaId: number;
  ordineId: number;
  numero: number;
  tipo: TipoDistinta;
  addebito: AddebitoDistinta;
  descrizione: string | null;
  stato: StatoDistinta;
  confermataAt: string | null;
  createdAt: string;
  ordine: { id: number; numero: string; stato: string; flussoCommerciale: string; societaId: number };
  confermataDa: { id: number; nome: string; cognome?: string | null } | null;
  _count: { righe: number };
}

interface TabDistinteProps {
  commessa: CommessaDettaglio;
  onRefresh: () => void;
  isBlocked: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—';

const etichettaDistinta = (d: DistintaListItem) => `${d.ordine?.numero ?? d.ordineId}-D${d.numero}`;

const TIPO_CFG: Record<TipoDistinta, { label: string; cls: string }> = {
  MERCE:       { label: 'Merce',       cls: 'bop-badge-info'    },
  LAVORAZIONI: { label: 'Lavorazioni', cls: 'bop-badge-neutral' },
};

const ADDEBITO_CFG: Record<AddebitoDistinta, { label: string; cls: string }> = {
  NOSTRO:  { label: 'A nostro carico',  cls: 'bop-badge-neutral' },
  CLIENTE: { label: 'A carico cliente', cls: 'bop-badge-warning' },
};

const STATO_CFG: Record<StatoDistinta, { label: string; cls: string }> = {
  BOZZA:      { label: 'Bozza',      cls: 'bop-badge-neutral' },
  CONFERMATA: { label: 'Confermata', cls: 'bop-badge-success' },
};

// ── Componente ────────────────────────────────────────────────────────────────

/**
 * TabDistinte — vista in SOLA LETTURA delle distinte della commessa.
 *
 * Le distinte non si creano più dalla commessa: nascono dalla scheda di un
 * ordine appalto confermato. Da qui si consultano e si apre la scheda
 * distinta in una nuova tab (pattern di TabOrdini).
 */
export default function TabDistinte({ commessa }: TabDistinteProps) {
  const { can } = usePermesso();
  const canView = can('commerciale.distinte', 'view');

  const [distinte, setDistinte] = useState<DistintaListItem[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  // Filtri client-side
  const [fTipo,  setFTipo]  = useState<'' | TipoDistinta>('');
  const [fStato, setFStato] = useState<'' | StatoDistinta>('');

  const load = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true); setError('');
    try {
      // @api GET /api/distinte?societaId={societaId}&commessaId={commessaId} -> {"success":true,"data":[{"id":"number","commessaId":"number","ordineId":"number","numero":"number","tipo":"MERCE|LAVORAZIONI","addebito":"NOSTRO|CLIENTE","descrizione":"string|null","stato":"BOZZA|CONFERMATA","confermataAt":"string|null","createdAt":"string","ordine":{"id":"number","numero":"string","stato":"string","flussoCommerciale":"string","societaId":"number"},"confermataDa":{"id":"number","nome":"string","cognome":"string|null"},"_count":{"righe":"number"}}]}
      const res = await apiFetch<{ success: boolean; data: DistintaListItem[] }>(
        `/api/distinte?societaId=${commessa.societaId}&commessaId=${commessa.id}`
      );
      setDistinte(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento delle distinte');
    } finally {
      setLoading(false);
    }
  }, [canView, commessa.societaId, commessa.id]);

  useEffect(() => { load(); }, [load]);

  if (!canView) {
    return <div className="bop-empty-state">Permesso Distinte necessario</div>;
  }

  if (loading) {
    return <div className="bop-empty-state">Caricamento distinte…</div>;
  }

  if (error) {
    return <div className="bop-text-danger">{error}</div>;
  }

  const filtered = distinte
    .filter(d => !fTipo  || d.tipo  === fTipo)
    .filter(d => !fStato || d.stato === fStato);

  const apriDistinta = (id: number) => {
    window.open('/commerciale/attivo/distinte/' + id, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bop-tab-distinte">
      {/* Filtri */}
      <div className="bop-filter-bar">
        <div className="bop-form-group">
          <label className="bop-label">Tipo</label>
          <select className="bop-select" value={fTipo}
            onChange={e => setFTipo(e.target.value as '' | TipoDistinta)}>
            <option value="">Tutti</option>
            <option value="MERCE">Merce</option>
            <option value="LAVORAZIONI">Lavorazioni</option>
          </select>
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Stato</label>
          <select className="bop-select" value={fStato}
            onChange={e => setFStato(e.target.value as '' | StatoDistinta)}>
            <option value="">Tutti</option>
            <option value="BOZZA">Bozza</option>
            <option value="CONFERMATA">Confermata</option>
          </select>
        </div>
      </div>

      {distinte.length === 0 ? (
        <div className="bop-empty-state">
          Nessuna distinta. Le distinte si creano dalla scheda di un ordine appalto confermato.
        </div>
      ) : filtered.length === 0 ? (
        <div className="bop-empty-state">Nessuna distinta corrispondente ai filtri.</div>
      ) : (
        <div className="bop-card">
          <table className="bop-table bop-table--clickable">
            <thead>
              <tr>
                <th>Distinta</th>
                <th>Ordine</th>
                <th>Tipo</th>
                <th>Addebito</th>
                <th className="bop-text-right">N. righe</th>
                <th>Stato</th>
                <th>Confermata il</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const tipo     = TIPO_CFG[d.tipo];
                const addebito = ADDEBITO_CFG[d.addebito];
                const stato    = STATO_CFG[d.stato];
                return (
                  <tr key={d.id} onClick={() => apriDistinta(d.id)}>
                    <td>
                      <strong>{etichettaDistinta(d)}</strong>
                      {d.descrizione && (
                        <>
                          <br />
                          <span className="bop-text-muted">{d.descrizione}</span>
                        </>
                      )}
                    </td>
                    <td>{d.ordine?.numero ?? '—'}</td>
                    <td><span className={`bop-badge ${tipo.cls}`}>{tipo.label}</span></td>
                    <td><span className={`bop-badge ${addebito.cls}`}>{addebito.label}</span></td>
                    <td className="bop-text-right">{d._count?.righe ?? 0}</td>
                    <td><span className={`bop-badge ${stato.cls}`}>{stato.label}</span></td>
                    <td>
                      {d.confermataAt ? (
                        <span>
                          {fmtDate(d.confermataAt)}
                          {d.confermataDa && (
                            <>
                              {' '}
                              <span className="bop-text-muted">
                                ({[d.confermataDa.nome, d.confermataDa.cognome].filter(Boolean).join(' ')})
                              </span>
                            </>
                          )}
                        </span>
                      ) : (
                        <span className="bop-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}
